/**
 * Cinematic AI Video generator.
 *
 * Generates one ~6s clip per photo via Seedance 1.5 Pro i2v (fal.ai). Every
 * clip is stored server-side, so individual clips can be regenerated without
 * re-running the whole listing. The clips are concatenated into a single MP4
 * in the browser with ffmpeg.wasm using the concat demuxer and `-c copy`
 * (no re-encode, lossless, near-instant).
 */

import { supabase } from "@/integrations/supabase/client";
import { RoomType } from "@/types/property";

export interface CinematicPhoto {
  url: string;
  roomType?: RoomType;
}

export type CinematicStyle = "walkthrough" | "drone";
export type CinematicResolution = "720p" | "1080p";

export interface CinematicClip {
  photoIndex: number;
  clipIndex: number;
  sourcePhotoUrl: string;
  roomType?: RoomType;
  status: "pending" | "ready" | "failed";
  url?: string;
  storagePath?: string;
  error?: string;
}

export interface CinematicVideoResult {
  success: boolean;
  videoUrl?: string;   // object URL for the stitched .mp4
  blob?: Blob;
  duration?: number;
  clips?: CinematicClip[];
  error?: string;
}

export const MAX_CLIPS = 10;
export const CLIP_SECONDS = 6;
const CLIP_BUCKET = "cinematic-clips";

const ROOM_PRIORITY: Record<RoomType, number> = {
  exterior: 0,
  living: 1,
  dining: 2,
  kitchen: 3,
  office: 4,
  bedroom: 5,
  bathroom: 6,
  laundry: 7,
  garage: 8,
  other: 9,
};

export function orderCinematicPhotos(photos: CinematicPhoto[]): CinematicPhoto[] {
  return [...photos]
    .sort((a, b) => {
      const ra = ROOM_PRIORITY[(a.roomType ?? "other") as RoomType] ?? 9;
      const rb = ROOM_PRIORITY[(b.roomType ?? "other") as RoomType] ?? 9;
      return ra - rb;
    })
    .slice(0, MAX_CLIPS);
}

/** Generate (or regenerate) exactly one clip. */
export async function generateSingleClip(params: {
  imageUrl: string;
  photoIndex: number;
  clipIndex: number;
  listingId?: string;
  roomType?: RoomType;
  style?: CinematicStyle;
  isRegeneration?: boolean;
}): Promise<{ url: string; storagePath?: string }> {
  const { data, error } = await supabase.functions.invoke("generate-cinematic-video", {
    body: {
      imageUrl: params.imageUrl,
      promptIndex: params.clipIndex,
      photoIndex: params.photoIndex,
      clipIndex: params.clipIndex,
      listingId: params.listingId,
      roomType: params.roomType,
      style: params.style ?? "walkthrough",
      isRegeneration: params.isRegeneration ?? false,
    },
  });
  if (error) throw new Error(error.message || "Edge function error");
  if (!data?.videoUrl) throw new Error(data?.error || "Cinematic clip generation failed");
  return { url: data.videoUrl as string, storagePath: data.storagePath as string | undefined };
}

/** Load already-generated clips for a listing, with fresh signed URLs. */
export async function loadListingClips(listingId: string): Promise<CinematicClip[]> {
  const { data, error } = await supabase
    .from("listing_video_clips")
    .select("photo_index, clip_index, source_photo_url, storage_path, room_type, status, error")
    .eq("listing_id", listingId)
    .order("clip_index", { ascending: true });
  if (error || !data) return [];

  const clips: CinematicClip[] = [];
  for (const row of data) {
    let url: string | undefined;
    if (row.storage_path) {
      const { data: signed } = await supabase.storage
        .from(CLIP_BUCKET)
        .createSignedUrl(row.storage_path, 3600);
      url = signed?.signedUrl;
    }
    clips.push({
      photoIndex: row.photo_index,
      clipIndex: row.clip_index,
      sourcePhotoUrl: row.source_photo_url,
      roomType: (row.room_type ?? undefined) as RoomType | undefined,
      status: (row.status as CinematicClip["status"]) ?? "pending",
      url,
      storagePath: row.storage_path ?? undefined,
      error: row.error ?? undefined,
    });
  }
  return clips;
}

/**
 * Concatenate MP4 clips losslessly into one MP4 using ffmpeg.wasm.
 * No re-encode: instant, no quality loss, and the output posts to Instagram,
 * Facebook and MLS portals (unlike the old .webm).
 */
export async function stitchClipsToMp4(
  clipUrls: string[]
): Promise<{ blob: Blob; objectUrl: string; duration: number }> {
  if (clipUrls.length === 0) throw new Error("No clips to stitch");

  const [{ FFmpeg }, { fetchFile }] = await Promise.all([
    import("@ffmpeg/ffmpeg"),
    import("@ffmpeg/util"),
  ]);

  const ffmpeg = new FFmpeg();
  const coreBase = "https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd";
  await ffmpeg.load({
    coreURL: `${coreBase}/ffmpeg-core.js`,
    wasmURL: `${coreBase}/ffmpeg-core.wasm`,
  });

  const names: string[] = [];
  for (let i = 0; i < clipUrls.length; i++) {
    const name = `clip${String(i).padStart(3, "0")}.mp4`;
    await ffmpeg.writeFile(name, await fetchFile(clipUrls[i]));
    names.push(name);
  }

  const listFile = names.map((n) => `file '${n}'`).join("\n");
  await ffmpeg.writeFile("concat.txt", new TextEncoder().encode(listFile));

  await ffmpeg.exec([
    "-f", "concat",
    "-safe", "0",
    "-i", "concat.txt",
    "-c", "copy",
    "-movflags", "+faststart",
    "output.mp4",
  ]);

  const out = await ffmpeg.readFile("output.mp4");
  const bytes = out instanceof Uint8Array ? out : new TextEncoder().encode(String(out));
  // Copy into a fresh buffer so the blob survives ffmpeg teardown.
  const blob = new Blob([new Uint8Array(bytes)], { type: "video/mp4" });

  try {
    ffmpeg.terminate();
  } catch {
    // ignore
  }

  return {
    blob,
    objectUrl: URL.createObjectURL(blob),
    duration: clipUrls.length * CLIP_SECONDS,
  };
}

export async function generateCinematicPropertyVideo(
  photos: CinematicPhoto[],
  listingId?: string,
  onProgress?: (info: {
    stage: "clips" | "stitching";
    clipsDone: number;
    clipsTotal: number;
    clips?: CinematicClip[];
  }) => void,
  style: CinematicStyle = "walkthrough"
): Promise<CinematicVideoResult> {
  try {
    if (!photos || photos.length === 0) {
      return { success: false, error: "No photos provided" };
    }

    const ordered = orderCinematicPhotos(photos);
    const total = ordered.length;

    const clips: CinematicClip[] = ordered.map((p, idx) => ({
      photoIndex: idx,
      clipIndex: idx,
      sourcePhotoUrl: p.url,
      roomType: p.roomType,
      status: "pending",
    }));

    onProgress?.({ stage: "clips", clipsDone: 0, clipsTotal: total, clips: [...clips] });

    let done = 0;
    await Promise.all(
      ordered.map(async (p, idx) => {
        try {
          const { url, storagePath } = await generateSingleClip({
            imageUrl: p.url,
            photoIndex: idx,
            clipIndex: idx,
            listingId,
            roomType: p.roomType,
            style,
          });
          clips[idx] = { ...clips[idx], status: "ready", url, storagePath };
        } catch (e) {
          clips[idx] = {
            ...clips[idx],
            status: "failed",
            error: e instanceof Error ? e.message : "Clip failed",
          };
        } finally {
          done += 1;
          onProgress?.({ stage: "clips", clipsDone: done, clipsTotal: total, clips: [...clips] });
        }
      })
    );

    const readyUrls = clips.filter((c) => c.status === "ready" && c.url).map((c) => c.url!);
    if (readyUrls.length === 0) {
      return { success: false, clips, error: clips[0]?.error || "All clips failed to generate" };
    }

    onProgress?.({ stage: "stitching", clipsDone: total, clipsTotal: total, clips: [...clips] });
    const { blob, objectUrl, duration } = await stitchClipsToMp4(readyUrls);

    return { success: true, videoUrl: objectUrl, blob, duration, clips };
  } catch (err) {
    console.error("Cinematic video generation failed:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error",
    };
  }
}
